/**
 * @module components/assistant/useAssistantNav
 * @description 어시스턴트 블록 → 기존 조회/상세 화면 router.push 래퍼.
 * - navigate: 상세 네비(resolveNav 허용목록 통과 location 만 이동, raw url/path push 금지)
 * - navigateList: 검색결과 '더보기'(목록+필터) 이동. 접수현황관리 권한 유무로 대상 화면을 갈라(권한 없으면
 *   접수이력), 위젯 검색조건을 대상 화면의 검색조건 스토어에 실어 동일 결과를 재현한다.
 * - 같은 라우트 재이동(NavigationDuplicated 류) 실패는 무해하므로 swallow
 * - 이동 후 패널은 열림 유지 (2026-06-05 사용자 결정)
 */
import { useRouter } from 'vue-router'
import { resolveNav } from './navRegistry'
import { useMenuStore } from '@/stores/menu'
import { useCriteriaStore } from '@/stores/criteria'
import { useSearchStore } from '@/stores/search'
import { useTabStore } from '@/stores/tabs'

// 더보기 대상 화면 — name 은 라우트 정의(경로 변경 내성), path 는 권한 판정/탭 dirty 키
const MANAGEMENT = { path: '/repair-request-management', name: '접수 현황 관리' }
const HISTORY = { path: '/repair-request-history', name: '접수 이력' }

function ymd(d) {
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * 어시스턴트 검색 filters 의 기간을 화면 스토어용으로 변환한다.
 * 어시스턴트가 기간 미지정(특정 고객 전체 등)으로 검색했으면 화면은 fromDate 가 필수라
 * 넓은 범위(오늘 -10년 ~ 오늘)로 근사해 '전체'에 가깝게 만든다.
 */
function dateRange(f) {
  const to = new Date()
  const from = new Date()
  from.setFullYear(from.getFullYear() - 10)
  return { fromDate: f.dateFrom || ymd(from), toDate: f.dateTo || ymd(to) }
}

/** filters → 접수현황관리(useCriteriaStore.RepairRequestManagement) 스키마 */
function managementCriteria(f) {
  const { fromDate, toDate } = dateRange(f)
  return {
    fromDate,
    toDate,
    type: 'productModelName',
    text: '',
    requestStatus: f.requestStatus || 'ENTIRE',
    areaMainCode: '',
    areaSubCode: '',
    customerName: f.customerName || '',
    customerPhoneNumber: f.customerPhoneNumber || '',
    serialNumber: ''
  }
}

/** filters → 접수이력(useSearchStore.HARepairRequestHistory) 스키마 (권한 폴백) */
function historyCriteria(f) {
  const { fromDate, toDate } = dateRange(f)
  return {
    fromDate,
    toDate,
    type: '',
    text: '',
    requestStatus: f.requestStatus || 'ENTIRE',
    areaMainCode: '',
    areaSubCode: '',
    customerName: f.customerName || '',
    customerPhoneNumber: f.customerPhoneNumber || '',
    page: '',
    size: ''
  }
}

export function useAssistantNav() {
  const router = useRouter()

  async function pushLoc(loc) {
    try {
      await router.push(loc)
    } catch (e) {
      const msg = (e && e.message) || ''
      if (e && e.name !== 'NavigationDuplicated' && !/duplicated|redundant|avoided/i.test(msg)) {
        if (window.__APP_LOG__) window.__APP_LOG__(e)
      }
    }
  }

  /**
   * 접수현황관리 화면 접근 권한(메뉴 트리) 여부. 권한이 없으면 상세/더보기 모두 접수이력으로 폴백한다.
   * @returns {Promise<boolean>}
   */
  async function canAccessManagement() {
    const menuStore = useMenuStore()
    try {
      await menuStore.loadMenu()
    } catch (e) {
      if (window.__APP_LOG__) window.__APP_LOG__(e)
    }
    return !!menuStore.findGroupByPath(MANAGEMENT.path)
  }

  /**
   * 블록 nav 메타로 상세 화면 이동을 시도한다.
   * 접수 상세(entity=repair)는 접수현황관리 권한이 없으면 접수이력 상세(repairHistory)로,
   * 상세 파라미터(id/partnerId)가 없으면 접수이력 목록으로 폴백한다.
   * @param {object} nav - { entity, id?, params? }
   * @returns {Promise<boolean>} 이동 시도 여부 (허용목록 외/필수값 누락이면 false)
   */
  async function navigate(nav) {
    let target = nav
    if (nav && nav.entity === 'repair' && !(await canAccessManagement())) {
      const p = (nav.params) || {}
      if (nav.id != null && nav.id !== '' && p.partnerId != null && p.partnerId !== '') {
        target = { entity: 'repairHistory', id: nav.id, params: { partnerId: p.partnerId } }
      } else {
        await pushLoc({ name: HISTORY.name }) // 상세 파라미터 없음 → 접수이력 목록
        return true
      }
    }
    const loc = resolveNav(target)
    if (!loc) return false
    await pushLoc(loc)
    return true
  }

  /**
   * 검색결과 '더보기' — 목록 화면으로 이동하며 위젯 검색조건을 필터로 전달한다.
   * 접수현황관리 접근 권한(메뉴 트리)이 없으면 접수이력으로 폴백한다.
   * @param {object} more - { entity:'repairList', label?, filters?: {customerName,customerPhoneNumber,requestStatus,dateFrom,dateTo} }
   * @returns {Promise<boolean>} 이동 시도 여부
   */
  async function navigateList(more) {
    if (!more || typeof more !== 'object') return false
    const filters = (more.filters && typeof more.filters === 'object') ? more.filters : {}
    const tabStore = useTabStore()
    const canManagement = await canAccessManagement()
    const target = canManagement ? MANAGEMENT : HISTORY

    if (canManagement) {
      useCriteriaStore().setSearchCriteria('RepairRequestManagement', managementCriteria(filters))
    } else {
      useSearchStore().setSearchCriteria('HARepairRequestHistory', historyCriteria(filters))
    }
    // keep-alive 로 이미 열린 탭이면 created 복원이 재실행되지 않으므로 dirty 표시 → activated 재검색
    tabStore.markDirty(target.path)
    await pushLoc({ name: target.name })
    return true
  }

  return { navigate, navigateList, canAccessManagement }
}
